// Robot Module - Pages
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Snackbar, Tabs, Tab, Divider } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useAppSelector } from '../store/hooks';
import { selectSelectedMuseum } from '../store/slices/museumSlice';
import { sendNHBLog } from '../shared/services/logging';
import { useRobots } from '../modules/robots/hooks/useRobots';
import { RobotStatsCards } from '../modules/robots/components/RobotStatsCards';
import { RobotCard } from '../modules/robots/components/RobotCard';
import { Robot } from '../modules/robots/types';
import { useTours } from '../modules/tour/hooks/useTours';
import { tourService } from '../modules/tour/services/tourService';
import { nhbApiService } from '../modules/robots/services/nhbApiService';
import { VideoCallSession, InitiateCallRequest, EndCallRequest } from '../modules/video-call/types';
import { useInitiateCall, useEndCall } from '../modules/video-call/hooks/useVideoCall';
import AgoraVideoCall from '../modules/video-call/components/AgoraVideoCall';

interface CallState {
  localVideoEnabled: boolean;
  localAudioEnabled: boolean;
  remoteVideoEnabled: boolean;
  remoteAudioEnabled: boolean;
  isConnected: boolean;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'unknown';
}

type TourId = string | number;

type RobotTour = {
  tour_id: TourId;
  tour_name: string;
  yaml_url: string;
  is_activated?: boolean;
  url?: string;
};

type RobotToursByRobotId = Record<string, RobotTour[]>;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown error';
};

const RobotManagementPage: React.FC = () => {
  const selectedMuseum = useAppSelector(selectSelectedMuseum);
  const user = useAppSelector(state => state.auth.user);
  const [installedTours, setInstalledTours] = useState<RobotToursByRobotId>({});
  const [activatedTours, setActivatedTours] = useState<RobotToursByRobotId>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [selectedRobotTab, setSelectedRobotTab] = useState(0);

  // Museum tab for "All Museums" mode
  const [activeMuseumTab, setActiveMuseumTab] = useState('all');

  useEffect(() => {
    setActiveMuseumTab('all');
  }, [selectedMuseum]);

  // Robot management, use selectedMuseum for filtering
  const { robots, loading, error, refreshRobots, getRobotStats } = useRobots();

  // Keep latest robots accessible without making effects depend on full robot objects
  const robotsRef = useRef<Robot[]>(robots);
  useEffect(() => {
    robotsRef.current = robots;
  }, [robots]);

  const selectedMuseumCode = selectedMuseum?.museum_code || '';
  const selectedMuseumName = selectedMuseum?.name || '';

  // Stable key so effects don't retrigger just because robot status fields changed
  // Sort to avoid retriggers if API returns the same robots in a different order.
  const robotIdsKey = useMemo(
    () =>
      robots
        .map(r => r.robot_id)
        .slice()
        .sort()
        .join('|'),
    [robots],
  );

  const museumCodeForRoutingMap = selectedMuseumCode || robots[selectedRobotTab]?.museum_code || '';

  // Keep selected tab valid. Do NOT reset to 0 on every status update.
  useEffect(() => {
    if (robots.length === 0) {
      if (selectedRobotTab !== 0) setSelectedRobotTab(0);
      return;
    }

    if (selectedRobotTab >= robots.length) {
      setSelectedRobotTab(0);
    }
  }, [robots.length, selectedRobotTab]);

  // Tours management, use selectedMuseum for tours
  const { data: tours } = useTours({ museumCode: selectedMuseum?.museum_code });

  // Debug tours loading
  console.log('🔍 Tours loaded:', tours);
  console.log('🔍 Selected museum:', selectedMuseum);

  // Video call states
  const [currentCall, setCurrentCall] = useState<VideoCallSession | null>(null);
  // Video call hooks
  const initiateCallMutation = useInitiateCall();
  const endCallMutation = useEndCall();

  // Check permissions
  const canInitiateCalls = true;

  // Listen for museum selection changes
  useEffect(() => {
    // All data fetch operations (robots, tours) use only Redux museum.
    // UI references Redux selectedMuseum only.
  }, [selectedMuseum]);

  // Handle loading state when no museum is selected
  useEffect(() => {
    if (!selectedMuseum) {
      // Don't set loading to false here, let the hook handle it
    }
  }, [selectedMuseum]);

  // Refetch robots on selectedMuseum change
  useEffect(() => {
    if (selectedMuseumCode && selectedMuseumName) {
      refreshRobots(selectedMuseumCode, selectedMuseumName);
    } else {
      // No museum selected means "all"; fetch all robots
      refreshRobots();
    }
  }, [selectedMuseumCode, selectedMuseumName, refreshRobots]);

  const handleCallRobot = async (robot: Robot) => {
    try {
      const userMuseumCode = (user as { museum_code?: string } | null)?.museum_code;
      const museumForLog = selectedMuseum?.museum_code || robot.museum_code || userMuseumCode || 'NIL';

      sendNHBLog({
        role: 'dashboard_user',
        robot: robot.robot_id,
        museum: museumForLog,
        task: 'dashboard_user_action',
        log: {
          action: 'call_robot_button',
          robot_id: robot.robot_id,
          robot_name: robot.name,
          museum_code: robot.museum_code,
        },
      }).catch(logError => {
        console.warn('Failed to log call robot action:', logError);
      });

      const callData: InitiateCallRequest = {
        robot_id: robot.robot_id,
        museum_code: robot.museum_code,
        call_reason: `Call to ${robot.name}`,
      };

      console.log('🚀 Initiating call with data:', callData);
      console.log('🎯 Expected channel name: Temi_' + robot.museum_code + '_' + robot.robot_id);

      const result = await initiateCallMutation.mutateAsync(callData);
      console.log('✅ Call initiated successfully:', result);
      setCurrentCall(result);
    } catch (error) {
      console.error('❌ Error initiating call:', error);
    }
  };

  const handleEndCall = async () => {
    if (!currentCall) return;
    try {
      const endData: EndCallRequest = {
        call_session_id: currentCall.id,
        reason: 'User ended call',
      };

      await endCallMutation.mutateAsync(endData);
      setCurrentCall(null);
    } catch (error) {
      console.error('Error ending call:', error);
    }
  };

  // Memoized so effects/handlers don't retrigger due to function identity changes
  const museumLevel = useMemo((): string => {
    if (selectedMuseum?.number_of_levels && Array.isArray(selectedMuseum.number_of_levels)) {
      return selectedMuseum.number_of_levels[0] || '1';
    }
    return '1';
  }, [selectedMuseum?.number_of_levels]);

  // Note: For installation, we use dashboard tours (tours created in the dashboard)
  // For activation, we use installed tours from API gateway (fetched in fetchInstalledToursForAllRobots)
  // So we don't need to fetch "available tours" from API gateway for installation

  // Fetch installed tours (for activation) from API gateway for all robots when robots are loaded
  // Note: We fetch both installed and activated tours
  // - Installed tours: tours that have been downloaded to the robot
  // - Activated tours: tours that are currently active (optimistic activation)
  useEffect(() => {
    const fetchInstalledToursForAllRobots = async () => {
      const robotsSnapshot = robotsRef.current;
      if (robotsSnapshot.length > 0) {
        const toursPromises = robotsSnapshot.map(async robot => {
          try {
            // Try API gateway first to get installed tours from the robot
            const museumCode = selectedMuseumCode;
            const apiResult = await nhbApiService.getRobotAvailableTours(robot.robot_id, museumCode);
            console.log('HELLO');
            // return;
            console.log(`🔍 Installed tours API response for ${robot.robot_id}:`, apiResult);

            // The API returns: { success: true, data: { installed_urls: [], active_urls: [], tours: [...] } }
            // Extract tours from the response
            let tourList: RobotTour[] = [];

            // First, try to get the transformed tours array
            if (apiResult?.data?.tours && Array.isArray(apiResult.data.tours)) {
              tourList = apiResult.data.tours as RobotTour[];
            }
            // Fallback to extracting from installed_urls and active_urls
            else if (apiResult?.data?.installed_urls && Array.isArray(apiResult.data.installed_urls)) {
              const installedUrls = apiResult.data.installed_urls || [];
              const activeUrls = apiResult.data.active_urls || [];

              // Create tour objects from URLs
              const installedTours = installedUrls.map((url: string) => {
                // Extract tour ID from URL - try multiple patterns
                // Pattern 1: Extract from filename like "lol-1-1.yaml" -> "lol-1-1"
                // Pattern 2: Extract from path like "-1-" -> "1"
                let tourId = 'unknown';
                const filename = url.split('/').pop() || '';
                if (filename) {
                  tourId = filename.replace('.yaml', '');
                } else {
                  const match = url.match(/-(\d+)-/);
                  if (match) {
                    tourId = match[1];
                  }
                }

                const isActive = activeUrls.includes(url);
                return {
                  tour_id: tourId,
                  tour_name: `Tour ${tourId}${isActive ? ' (Active)' : ''}`,
                  yaml_url: url,
                  is_activated: isActive,
                };
              });

              tourList = installedTours;
            }
            // Legacy format support
            else if (apiResult?.data?.tours?.available) {
              tourList = apiResult.data.tours.available as RobotTour[];
            } else if (apiResult?.tours?.available) {
              tourList = apiResult.tours.available as RobotTour[];
            } else if (Array.isArray(apiResult)) {
              tourList = apiResult as RobotTour[];
            } else if (apiResult?.data && Array.isArray(apiResult.data)) {
              tourList = apiResult.data as RobotTour[];
            } else if (apiResult?.tours && Array.isArray(apiResult.tours)) {
              tourList = apiResult.tours as RobotTour[];
            }

            console.log(`✅ Parsed installed tours for ${robot.robot_id}:`, tourList);

            // Extract activated tours from active_urls
            let activatedTourList: RobotTour[] = [];
            if (apiResult?.data?.active_urls && Array.isArray(apiResult.data.active_urls)) {
              activatedTourList = apiResult.data.active_urls.map((url: string) => {
                // Extract tour ID from URL - try multiple patterns
                let tourId = 'unknown';
                const filename = url.split('/').pop() || '';
                if (filename) {
                  tourId = filename.replace('.yaml', '');
                } else {
                  const match = url.match(/-(\d+)-/);
                  if (match) {
                    tourId = match[1];
                  }
                }
                return {
                  tour_id: tourId,
                  tour_name: `Tour ${tourId}`,
                  yaml_url: url,
                  is_activated: true,
                };
              });
              console.log(`✅ Parsed activated tours for ${robot.robot_id}:`, activatedTourList);
            }

            // Mark installed tours as activated if they're in active_urls
            tourList = tourList.map(tour => {
              const isActivated = activatedTourList.some(
                at => at.yaml_url === tour.yaml_url || (tour.url ? at.yaml_url === tour.url : false),
              );
              return {
                ...tour,
                is_activated: isActivated,
                tour_name: isActivated
                  ? `${tour.tour_name || `Tour ${tour.tour_id}`} (Active)`
                  : tour.tour_name || `Tour ${tour.tour_id}`,
              };
            });

            // If API gateway doesn't return tours, fallback to our database
            if (tourList.length === 0 && selectedMuseumCode) {
              const dbResult = await tourService.getInstalledTours(robot.robot_id, selectedMuseumCode, museumLevel);
              // Convert tour URLs to a format that RobotCard can display
              tourList = dbResult.tour_urls.map((url: string) => {
                let tourId = 'unknown';
                const filename = url.split('/').pop() || '';
                if (filename) {
                  tourId = filename.replace('.yaml', '');
                } else {
                  const match = url.match(/-(\d+)-/);
                  if (match) {
                    tourId = match[1];
                  } else {
                    tourId = url;
                  }
                }
                const isActivated = activatedTourList.some(at => at.yaml_url === url);
                return {
                  tour_id: tourId,
                  tour_name: isActivated ? `Tour ${tourId} (Active)` : `Tour ${tourId}`,
                  yaml_url: url,
                  is_activated: isActivated,
                };
              });
            }

            return {
              robotId: robot.robot_id,
              tours: tourList,
              activatedTours: activatedTourList,
            };
          } catch (error) {
            console.error(`Error fetching installed tours for robot ${robot.robot_id}:`, error);
            // Fallback to database if API fails
            if (selectedMuseumCode) {
              try {
                const dbResult = await tourService.getInstalledTours(robot.robot_id, selectedMuseumCode, museumLevel);
                const tourList = dbResult.tour_urls.map((url: string) => {
                  const match = url.match(/-(\d+)-/);
                  const tourId = match ? match[1] : url;
                  return {
                    tour_id: tourId,
                    tour_name: `Tour ${tourId}`,
                    yaml_url: url,
                  };
                });
                return {
                  robotId: robot.robot_id,
                  tours: tourList,
                  activatedTours: [],
                };
              } catch (dbError) {
                console.error(`Error fetching installed tours from DB for robot ${robot.robot_id}:`, dbError);
              }
            }
          }
          return {
            robotId: robot.robot_id,
            tours: [],
            activatedTours: [],
          };
        });

        const results = (await Promise.all(toursPromises)) as Array<{
          robotId: string;
          tours: RobotTour[];
          activatedTours: RobotTour[];
        }>;
        const newInstalledTours: RobotToursByRobotId = {};
        const newActivatedTours: RobotToursByRobotId = {};

        results.forEach(({ robotId, tours, activatedTours }) => {
          newInstalledTours[robotId] = tours;
          if (activatedTours) {
            newActivatedTours[robotId] = activatedTours;
          }
        });

        console.log('🔍 Final installedTours state:', newInstalledTours);
        console.log('🔍 Final activatedTours state:', newActivatedTours);
        setInstalledTours(newInstalledTours);
        setActivatedTours(newActivatedTours);
      }
    };

    fetchInstalledToursForAllRobots();
  }, [robotIdsKey, selectedMuseumCode, museumLevel]);

  // Smart refresh: Only refresh when needed, not on a fixed 5-second timer
  // This prevents blinking and doesn't interfere with video calls
  // Refresh happens:
  // 1. When museum selection changes (already handled above)
  // 2. When user clicks refresh button (handled by handleRefresh)
  // 3. Periodic check every 30 seconds (much less intrusive than 5 seconds)
  // 4. NEVER during active video calls

  useEffect(() => {
    // Don't auto-refresh if there's an active call to prevent interference
    if (currentCall) {
      console.log('⏸️ Auto-refresh paused during active video call');
      return;
    }

    // Use a longer interval (30 seconds) to check for robot status changes
    // This is much less intrusive than 5 seconds and prevents blinking
    const interval = setInterval(() => {
      console.log('🔄 Periodic robot status check (30s interval)...');
      if (selectedMuseumCode && selectedMuseumName) {
        refreshRobots(selectedMuseumCode, selectedMuseumName, { silent: true });
      } else {
        refreshRobots(undefined, undefined, { silent: true }); // Refresh all robots
      }
    }, 30000); // Refresh every 30 seconds instead of 5

    return () => clearInterval(interval);
  }, [selectedMuseumCode, selectedMuseumName, refreshRobots, currentCall]);

  const handleRefresh = () => {
    if (selectedMuseum && selectedMuseum.museum_code && selectedMuseum.name) {
      console.log(
        '🔄 Refresh button clicked - using museum code:',
        selectedMuseum.museum_code,
        'for museum:',
        selectedMuseum.name,
      );
      refreshRobots(selectedMuseum.museum_code, selectedMuseum.name);
    } else {
      console.log('🔄 Refresh button clicked - refreshing all robots');
      refreshRobots();
    }
  };

  const handleInstallTour = async (robot: Robot, tourId: number) => {
    if (!selectedMuseum?.museum_code) {
      setSnackbarMessage('Please select a museum first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // For installation, use tours from dashboard (created tours)
    const tour = tours?.find(t => t.id === tourId);

    if (!tour) {
      setSnackbarMessage('Tour not found');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      // Generate YAML URL from dashboard tour
      const tourYamlUrl = tourService.getTourYamlUrl(tour, selectedMuseum.museum_code, museumLevel);

      const result = await tourService.installTours(robot.robot_id, selectedMuseum.museum_code, museumLevel, [
        tourYamlUrl,
      ]);

      if (result.success) {
        setSnackbarMessage(`Tour ${tour.tour_id} installation initiated successfully`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Refresh installed tours after a short delay
        setTimeout(() => {
          handleRefreshInstalledTours(robot);
        }, 2000);
      } else {
        setSnackbarMessage(result.error || 'Failed to install tour');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: unknown) {
      console.error('Error installing tour:', error);
      setSnackbarMessage(getErrorMessage(error) || 'Failed to install tour');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleActivateTour = async (robot: Robot, tourUrl: string) => {
    if (!selectedMuseum?.museum_code) {
      setSnackbarMessage('Please select a museum first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await tourService.activateTours(robot.robot_id, selectedMuseum.museum_code, museumLevel, [tourUrl]);

      setSnackbarMessage('Tour activated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Refresh installed tours after activation
      setTimeout(() => {
        handleRefreshInstalledTours(robot);
      }, 1000);
    } catch (error: unknown) {
      console.error('Error activating tour:', error);
      setSnackbarMessage(getErrorMessage(error) || 'Failed to activate tour');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleRefreshInstalledTours = async (robot: Robot) => {
    try {
      // Try API gateway first to get installed tours
      const museumCode = selectedMuseum?.museum_code || '';
      const apiResult = await nhbApiService.getRobotAvailableTours(robot.robot_id, museumCode);
      console.log(`🔍 Refresh installed tours API response for ${robot.robot_id}:`, apiResult);

      // Extract tours from the response (handles both new format with installed_urls and legacy format)
      let tourList: RobotTour[] = [];
      if (apiResult?.data?.tours && Array.isArray(apiResult.data.tours)) {
        tourList = apiResult.data.tours as RobotTour[];
      } else if (apiResult?.data?.installed_urls && Array.isArray(apiResult.data.installed_urls)) {
        const installedUrls = apiResult.data.installed_urls || [];
        const activeUrls = apiResult.data.active_urls || [];
        tourList = installedUrls.map((url: string) => {
          const match = url.match(/-(\d+)-/);
          const tourId = match ? match[1] : url.split('/').pop()?.replace('.yaml', '') || 'unknown';
          const isActivated = activeUrls.includes(url);
          return {
            tour_id: tourId,
            tour_name: isActivated ? `Tour ${tourId} (Active)` : `Tour ${tourId}`,
            yaml_url: url,
            is_activated: isActivated,
          };
        });
      } else if (apiResult?.data?.tours?.available) {
        tourList = apiResult.data.tours.available as RobotTour[];
      } else if (apiResult?.tours?.available) {
        tourList = apiResult.tours.available as RobotTour[];
      } else if (Array.isArray(apiResult)) {
        tourList = apiResult as RobotTour[];
      } else if (apiResult?.data && Array.isArray(apiResult.data)) {
        tourList = apiResult.data as RobotTour[];
      } else if (apiResult?.tours && Array.isArray(apiResult.tours)) {
        tourList = apiResult.tours as RobotTour[];
      }

      // Extract activated tours
      let activatedTourList: RobotTour[] = [];
      if (apiResult?.data?.active_urls && Array.isArray(apiResult.data.active_urls)) {
        activatedTourList = apiResult.data.active_urls.map((url: string) => {
          const match = url.match(/-(\d+)-/);
          const tourId = match ? match[1] : url.split('/').pop()?.replace('.yaml', '') || 'unknown';
          return {
            tour_id: tourId,
            tour_name: `Tour ${tourId}`,
            yaml_url: url,
            is_activated: true,
          };
        });
      }

      // If API gateway doesn't return tours, fallback to our database
      if (tourList.length === 0 && selectedMuseum?.museum_code) {
        const dbResult = await tourService.getInstalledTours(robot.robot_id, selectedMuseum.museum_code, museumLevel);
        tourList = dbResult.tour_urls.map((url: string) => {
          const match = url.match(/-(\d+)-/);
          const tourId = match ? match[1] : url;
          const isActivated = activatedTourList.some(at => at.yaml_url === url);
          return {
            tour_id: tourId,
            tour_name: isActivated ? `Tour ${tourId} (Active)` : `Tour ${tourId}`,
            yaml_url: url,
            is_activated: isActivated,
          };
        });
      }

      setInstalledTours(prev => ({
        ...prev,
        [robot.robot_id]: tourList,
      }));

      setActivatedTours(prev => ({
        ...prev,
        [robot.robot_id]: activatedTourList,
      }));
    } catch (error) {
      console.error(`Error refreshing installed tours for robot ${robot.robot_id}:`, error);
      // Fallback to database if API fails
      if (selectedMuseum?.museum_code) {
        try {
          const dbResult = await tourService.getInstalledTours(robot.robot_id, selectedMuseum.museum_code, museumLevel);
          const tourList = dbResult.tour_urls.map((url: string) => {
            const match = url.match(/-(\d+)-/);
            const tourId = match ? match[1] : url;
            return {
              tour_id: tourId,
              tour_name: `Tour ${tourId}`,
              yaml_url: url,
            };
          });
          setInstalledTours(prev => ({
            ...prev,
            [robot.robot_id]: tourList,
          }));
        } catch (dbError) {
          console.error(`Error refreshing installed tours from DB for robot ${robot.robot_id}:`, dbError);
        }
      }
    }
  };

  // Group robots by museum for "All Museums" tab view
  const robotMuseumGroups = useMemo(() => {
    const grouped = robots.reduce<Record<string, typeof robots>>((acc, robot) => {
      const key = robot.museum_code || 'Unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(robot);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [robots]);

  // Only show the full-page spinner on initial load.
  // During background refreshes we keep the UI stable to avoid "page rerender/blink".
  if (loading && robots.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {selectedMuseum ? `${selectedMuseum.name} - Robot Management` : 'Robot Management'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
            Refresh Robots
          </Button>
        </Box>
      </Box>
      {!selectedMuseum && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Showing all robots across all museums. Select a specific museum from the navigation menu to filter.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <>
        {/* Statistics */}
        <RobotStatsCards stats={getRobotStats()} />

        {/* Current Call Interface */}
        {currentCall && (
          <Box sx={{ mb: 3, height: '65vh', minHeight: 450, maxHeight: 700 }}>
            <AgoraVideoCall
              callSession={currentCall}
              onEndCall={handleEndCall}
              onCallStateChange={(state: CallState) => {
                console.log('📞 Call state changed:', state);
              }}
            />
          </Box>
        )}

        {/* Robot List — single museum: flat cards; All Museums: tabs + grouped cards */}
        {!selectedMuseum && robots.length > 0 ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={activeMuseumTab}
                onChange={(_e, val) => setActiveMuseumTab(val)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="All" value="all" />
                {robotMuseumGroups.map(([museumCode, museumRobots]) => (
                  <Tab key={museumCode} label={`${museumCode} (${museumRobots.length})`} value={museumCode} />
                ))}
              </Tabs>
            </Box>
            {robotMuseumGroups
              .filter(([museumCode]) => activeMuseumTab === 'all' || museumCode === activeMuseumTab)
              .map(([museumCode, museumRobots], index) => (
                <Box key={museumCode}>
                  {index > 0 && <Divider sx={{ my: 3 }} />}
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {museumCode}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {museumRobots.map(robot => (
                      <Box key={robot.id} sx={{ flex: '1 1 300px', minWidth: '300px', maxWidth: '400px' }}>
                        <RobotCard
                          robot={robot}
                          canInitiateCalls={canInitiateCalls}
                          onCallRobot={handleCallRobot}
                          availableTours={tours || []}
                          installedTours={installedTours[robot.robot_id] || []}
                          activatedTours={activatedTours[robot.robot_id] || []}
                          onInstallTour={handleInstallTour}
                          onActivateTour={handleActivateTour}
                          onRefreshInstalledTours={handleRefreshInstalledTours}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
          </>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {robots.map(robot => (
              <Box key={robot.id} sx={{ flex: '1 1 300px', minWidth: '300px', maxWidth: '400px' }}>
                <RobotCard
                  robot={robot}
                  canInitiateCalls={canInitiateCalls}
                  onCallRobot={handleCallRobot}
                  availableTours={tours || []}
                  installedTours={installedTours[robot.robot_id] || []}
                  activatedTours={activatedTours[robot.robot_id] || []}
                  onInstallTour={handleInstallTour}
                  onActivateTour={handleActivateTour}
                  onRefreshInstalledTours={handleRefreshInstalledTours}
                />
              </Box>
            ))}
          </Box>
        )}

        {robots.length === 0 && !loading && (
          <Alert severity="info">{selectedMuseum ? 'No robots found for this museum.' : 'No robots found.'}</Alert>
        )}
      </>
      {/* Robot Location Map with Tab Selection */}
      <Box sx={{ mt: 4, mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Robot Location
        </Typography>
        {robots.length > 0 && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={selectedRobotTab}
                onChange={(_event: React.SyntheticEvent, newValue: number) => setSelectedRobotTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="robot location tabs"
              >
                {robots.map(robot => (
                  <Tab key={robot.robot_id} label={robot.name || robot.robot_id} />
                ))}
              </Tabs>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: '600px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <iframe
                src={`${process.env.REACT_APP_RAAS_BASE_URL || 'https://nhb-raas-staging.senserbot.com'}/api-ws/routing/?robot=${encodeURIComponent(robots[selectedRobotTab]?.robot_id || '')}&hide=true&showLocation=true&scale=90&museum=${encodeURIComponent(museumCodeForRoutingMap)}`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title="Robot Location Map"
              />
            </Box>
          </>
        )}
        {robots.length === 0 && <Alert severity="info">No robots available to display location map.</Alert>}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RobotManagementPage;
